import { compile, createApp } from 'vue';
import store from '../store';
import router from '../router';
import { setApiLanguageIdResolver } from '../utils/apiLanguageId.js';
import { startAttributionCaptureLifecycle, runAttributionCapture } from '../attribution/capture.js';
import { getRootAppComponents, registerGlobalComponents } from './registerGlobalComponents';
import { mountVueIslands } from './mountIslands';
import { registerWindowApi, installLegacyDomEnhancements } from './legacyPageBindings';
import { markAppReady } from './maintenanceAndSplash';
import { scheduleIdleWork } from '../utils/scheduleIdleWork.js';
import { pageNeedsVuetify } from '../utils/pageNeedsVuetify.js';
import { applyAxiosCsrfToken } from '../bootstrap.js';
import {
    initBlogArticleToc,
    initBlogImageLightbox,
    initFaqDetailsGroups,
    initHeaderNavDropdowns,
    registerShowCountryMethods,
} from './pageScripts.js';
import { initValidityCheckForm } from './validityCheckForm.js';

function readBootstrapFromWindow() {
    return typeof window !== 'undefined' ? window.__APP_DATA__ : null;
}

function isMapEmbedShellPage() {
    return typeof document !== 'undefined' && Boolean(document.getElementById('map-embed-app'));
}

let cachedRootTemplateSource = null;
let cachedRootRender = null;

function sanitizeAppRoot(rootEl) {
    rootEl.querySelectorAll('script, style').forEach((node) => node.remove());
}

function rememberSsrPlate(rootEl) {
    try {
        const typed = rootEl.querySelector('[data-home-license-plate-search-ssr="1"] input');
        if (typed && typed.value) {
            window.__CV_SSR_PLATE = typed.value;
        }
    } catch (e) {}
}

function captureRootTemplateSource(rootEl) {
    if (cachedRootTemplateSource !== null) {
        return cachedRootTemplateSource;
    }

    rememberSsrPlate(rootEl);
    sanitizeAppRoot(rootEl);
    cachedRootTemplateSource = rootEl.innerHTML.trim();
    return cachedRootTemplateSource;
}

function buildRootRender(rootEl) {
    if (cachedRootRender) {
        return cachedRootRender;
    }

    const source = captureRootTemplateSource(rootEl);
    if (!source) {
        if (typeof console !== 'undefined' && console.error && !isMapEmbedShellPage()) {
            console.error('[app] #app template is empty after sanitization.');
        }
        return null;
    }

    try {
        if (typeof compile !== 'function') {
            if (typeof console !== 'undefined' && console.error) {
                console.error('[app] Vue compiler missing — rebuild assets (vue.esm-bundler.js).');
            }
            return null;
        }
        cachedRootRender = compile(source);
        return cachedRootRender;
    } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] #app template compile failed', err);
        }
        return null;
    }
}

function createRootComponentOptions(vuetify, resolveVuetifyLocale, render) {
    return {
        components: getRootAppComponents(),
        render,
        mounted() {
            if (vuetify) {
                const appData = window.__APP_DATA__;
                if (appData) {
                    vuetify.locale.current.value = resolveVuetifyLocale(appData.lang?.code);
                }

                this.$watch(
                    () => this.$store.getters.lang?.code,
                    (nextLangCode) => {
                        vuetify.locale.current.value = resolveVuetifyLocale(nextLangCode);
                    }
                );
            }

            this.$watch(
                () => this.$store.getters.lang?.id,
                (newId, oldId) => {
                    if (oldId == null || newId == null || newId === oldId) return;
                    if (typeof localStorage !== 'undefined') {
                        localStorage.setItem('langId', String(newId));
                    }
                    this.$store.dispatch('form/reloadCountries').catch(() => {});
                    this.$store.dispatch('form/reloadProductsCatalog').catch(() => {});
                }
            );

            // Not deferred: until this runs the form falls back to a native GET submit.
            initValidityCheckForm();

            scheduleIdleWork(() => {
                initHeaderNavDropdowns();
                initBlogArticleToc();
                initBlogImageLightbox();
                initFaqDetailsGroups();
                registerShowCountryMethods();
            });
        },
    };
}

function mountMainVueApp(app, rootEl) {
    if (!rootEl) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] #app not found in DOM — Vue not mounted (no API-driven widgets).');
        }
        markAppReady();
        return;
    }

    // Cross-bundle guard: duplicate app.js loads share window, not module state.
    if (rootEl.__vue_app__ || window.__CV_MAIN_VUE_MOUNTED__ || window.__CV_MAIN_VUE_MOUNTING__) {
        markAppReady();
        return;
    }

    // Map embed (and similar) use an empty #app shell — mounting would set template:'' and warn.
    if (!rootEl.innerHTML.trim()) {
        window.__CV_MAIN_VUE_MOUNTED__ = true;
        markAppReady();
        return;
    }

    try {
        window.__CV_MAIN_VUE_MOUNTING__ = true;
        app.mount(rootEl);
        window.__CV_MAIN_VUE_MOUNTED__ = true;
    } catch (err) {
        window.__CV_MAIN_VUE_MOUNTED__ = false;
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] #app mount failed', err);
        }
    } finally {
        window.__CV_MAIN_VUE_MOUNTING__ = false;
        markAppReady();
    }
}

function mountVueApplication(vuetify, resolveVuetifyLocale) {
    if (isMapEmbedShellPage()) {
        mountVueIslands();
        markAppReady();
        return;
    }

    const rootEl = document.querySelector('#app');
    if (!rootEl) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] #app not found in DOM — Vue not mounted (no API-driven widgets).');
        }
        mountVueIslands();
        return;
    }

    const render = buildRootRender(rootEl);
    if (!render) {
        markAppReady();
        mountVueIslands();
        return;
    }

    const app = createApp(createRootComponentOptions(vuetify, resolveVuetifyLocale, render));

    app.use(store);
    if (vuetify) {
        app.use(vuetify);
    }
    app.use(router);
    registerGlobalComponents(app);

    if (typeof document !== 'undefined' && document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            () => mountMainVueApp(app, rootEl),
            { once: true }
        );
    } else {
        mountMainVueApp(app, rootEl);
    }

    mountVueIslands();
}

/** Max wait on buy-vignette before mounting without Vuetify. */
const VUETIFY_MOUNT_FALLBACK_MS = 1200;

/**
 * Boots Vue, Vuex, router, Blade islands, and legacy DOM helpers.
 * Pages without buy-vignette mount immediately and do not load Vuetify.
 * The hero calendar loads Vuetify when it opens. Buy-vignette still prefers
 * Vuetify at startup, with a short fallback.
 */
export function runApplication() {
    try {
        // Prevent duplicate boot when app.js is evaluated twice (cached + versioned URL).
        if (typeof window !== 'undefined' && window.__CV_APP_BOOTED__) {
            markAppReady();
            return;
        }
        if (typeof window !== 'undefined') {
            window.__CV_APP_BOOTED__ = true;
        }

        applyAxiosCsrfToken();

        const bootstrapAppData = readBootstrapFromWindow();

        registerWindowApi(store);
        store.dispatch('bootstrapFromWindow');

        if (bootstrapAppData && !bootstrapAppData.euroOnlyCurrency && !bootstrapAppData.secondHomeFunnel) {
            store.dispatch('form/reloadProductsCatalog').catch(() => {});
        }

        setApiLanguageIdResolver(() => {
            const id = store.state.lang?.id;
            if (id !== undefined && id !== null && id !== '') return id;
            if (typeof localStorage !== 'undefined') {
                return localStorage.getItem('langId');
            }
            return null;
        });

        scheduleIdleWork(() => {
            store.dispatch('loadLanguages').catch(() => {});
        });

        scheduleIdleWork(() => {
            startAttributionCaptureLifecycle();
            router.afterEach(() => {
                runAttributionCapture();
            });
        });

        scheduleIdleWork(() => {
            installLegacyDomEnhancements();
        });

        if (!pageNeedsVuetify()) {
            mountVueApplication(null, () => 'en');
            return;
        }

        let mounted = false;
        const bootApp = (vuetify, resolveVuetifyLocale) => {
            if (mounted) return;
            mounted = true;
            mountVueApplication(vuetify, resolveVuetifyLocale ?? (() => 'en'));
        };

        const vuetifyFallbackTimer = setTimeout(() => bootApp(null), VUETIFY_MOUNT_FALLBACK_MS);

        import(/* webpackChunkName: 'vuetify-boot' */ '../plugins/vuetify.js')
            .then(({ createAppVuetify, resolveVuetifyLocale }) => {
                clearTimeout(vuetifyFallbackTimer);
                bootApp(createAppVuetify(bootstrapAppData?.lang?.code), resolveVuetifyLocale);
            })
            .catch((err) => {
                clearTimeout(vuetifyFallbackTimer);
                if (typeof console !== 'undefined' && console.error) {
                    console.error('[app] Vuetify bootstrap failed — mounting without Vuetify', err);
                }
                bootApp(null);
            });
    } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
            console.error('[app] runApplication failed', err);
        }
        markAppReady();
    }
}
